import { blobToBytes } from '../../core/blob-bytes';
import type { ConvertContext, Converter } from '../../core/converter';
import { EngineFailure } from '../../core/errors';
import { canonicalExtension, buildOutputName } from '../../core/filename';
import { extensionOf } from '../../core/file-detect';
import { documentFidelity } from '../../core/fidelity';
import { sniffFormat } from '../../core/magic-bytes';
import { LOCAL_CONVERSION_COST } from '../../core/pricing';
import type { ConversionResult, ConverterCapabilities, ConvertOptions, FileMetadata } from '../../core/types';
import { verifyDocumentOutput } from '../../core/verify';
import {
  jsonToDelimited,
  jsonToXml,
  jsonToYaml,
  parseDelimited,
  parseJson,
  parseXmlText,
  parseYamlText,
  stableStringify,
} from './structured-codec';
import { decodeTextFile, normalizeNewlines } from './text-codec';

export const STRUCTURED_SOURCE_FORMATS = ['csv', 'tsv', 'json', 'yaml', 'xml'];
export const STRUCTURED_TARGET_FORMATS = ['csv', 'json', 'yaml', 'xml'];

/** 允许的转换组合：能力声明与运行期判定共用同一张表，避免两处漂移。 */
const SUPPORTED_PAIRS = new Set(['csv>json', 'tsv>json', 'json>csv', 'json>yaml', 'yaml>json', 'json>xml', 'xml>json']);

const MIME_BY_FORMAT: Record<string, string> = {
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  json: 'application/json',
  yaml: 'application/yaml',
  xml: 'application/xml',
};

function throwIfAborted(context?: ConvertContext): void {
  if (context?.signal?.aborted) {
    const error = new Error('任务已取消');
    error.name = 'AbortError';
    throw error;
  }
}

/** CSV/TSV ↔ JSON、JSON ↔ YAML/XML。 */
export class StructuredDocumentConverter implements Converter {
  capabilities(): ConverterCapabilities {
    return {
      id: 'document.structured',
      category: 'document',
      sourceFormats: [...STRUCTURED_SOURCE_FORMATS],
      targetFormats: [...STRUCTURED_TARGET_FORMATS],
      local: true,
      cloud: false,
      defaultFidelity: 'L1',
      mode: 'local',
      requiresUpload: false,
      estimatedCost: LOCAL_CONVERSION_COST,
    };
  }

  canConvert(source: FileMetadata, targetExtension: string): boolean {
    const sourceExtension = canonicalExtension(source.type.extension);
    const target = canonicalExtension(targetExtension);
    return SUPPORTED_PAIRS.has(`${sourceExtension}>${target}`);
  }

  async inspect(file: File): Promise<FileMetadata> {
    const bytes = await blobToBytes(file);
    const detected = sniffFormat(bytes);
    return {
      name: file.name,
      size: file.size,
      type: {
        extension: detected?.extension ?? extensionOf(file.name),
        mime: detected?.mime ?? file.type ?? null,
        category: 'document',
      },
    };
  }

  async convert(file: File, options: ConvertOptions = {}, context?: ConvertContext): Promise<ConversionResult> {
    throwIfAborted(context);
    context?.onProgress?.({ progress: 15, stage: '读取文本' });
    const bytes = await blobToBytes(file);
    const source = resolveSourceFormat(bytes, file.name);
    const target = canonicalExtension(String(options.targetFormat ?? 'json'));

    if (!SUPPORTED_PAIRS.has(`${source}>${target}`)) {
      throw new EngineFailure('UNSUPPORTED_CONVERSION', `结构化引擎不支持 ${source} → ${target}`);
    }

    const { text } = decodeTextFile(bytes);
    const content = normalizeNewlines(text).trim();
    if (!content) throw new EngineFailure('MALFORMED_FILE', '源文件内容为空');

    throwIfAborted(context);
    context?.onProgress?.({ progress: 45, stage: '转换' });

    let structurePreserved = true;
    let output = '';
    let parsedForVerification: unknown;
    const assertions: Array<{ label: string; ok: boolean }> = [];

    switch (`${source}>${target}`) {
      case 'csv>json':
      case 'tsv>json': {
        const parsed = parseDelimited(content, source === 'tsv' ? '\t' : undefined);
        parsedForVerification = parsed.rows;
        const outputValue: unknown = parsed.rows;
        output = `${JSON.stringify(outputValue, null, 2)}\n`;
        assertions.push({ label: '行数与源表格一致', ok: parsed.rows.length === (JSON.parse(output) as unknown[]).length });
        assertions.push({ label: '列名与源表头一致', ok: compareFields(parsed.fields, Object.keys(parsed.rows[0] ?? {})) });
        break;
      }
      case 'json>csv': {
        const value = parseJson(content);
        const result = jsonToDelimited(value);
        structurePreserved = result.structurePreserved;
        output = result.csv;
        parsedForVerification = parseDelimited(output, ',').rows;
        assertions.push({ label: '产物可被 CSV 解析器读取', ok: true });
        assertions.push({
          label: '行数与源记录数一致',
          ok: (Array.isArray(value) ? value.length : 1) === (parsedForVerification as unknown[]).length,
        });
        break;
      }
      case 'json>yaml': {
        const value = parseJson(content);
        output = jsonToYaml(value);
        const reparsed = parseYamlText(output);
        parsedForVerification = reparsed;
        assertions.push({ label: '数据模型与源 JSON 一致', ok: stableStringify(reparsed) === stableStringify(value) });
        break;
      }
      case 'yaml>json': {
        const value = parseYamlText(content);
        output = `${JSON.stringify(value, null, 2)}\n`;
        parsedForVerification = JSON.parse(output) as unknown;
        assertions.push({ label: '数据模型与源 YAML 一致', ok: stableStringify(parsedForVerification) === stableStringify(value) });
        break;
      }
      case 'json>xml': {
        const value = parseJson(content);
        structurePreserved = false;
        output = jsonToXml(value);
        const reparsed = parseXmlText(output);
        parsedForVerification = reparsed;
        assertions.push({ label: '产物为合法 XML', ok: !!reparsed && typeof reparsed === 'object' });
        break;
      }
      case 'xml>json': {
        const value = parseXmlText(content);
        structurePreserved = false;
        output = `${JSON.stringify(value, null, 2)}\n`;
        parsedForVerification = JSON.parse(output) as unknown;
        assertions.push({ label: '产物为合法 JSON', ok: !!parsedForVerification && typeof parsedForVerification === 'object' });
        break;
      }
      default:
        throw new EngineFailure('UNSUPPORTED_CONVERSION', `未实现的转换：${source} → ${target}`);
    }

    throwIfAborted(context);
    context?.onProgress?.({ progress: 75, stage: '校验' });
    const verification = verifyDocumentOutput({ parsed: parsedForVerification, assertions });

    const fidelity = documentFidelity({ sourceExtension: source, targetExtension: target, structurePreserved });
    const mime = MIME_BY_FORMAT[target] ?? 'text/plain';
    const fileName = buildOutputName(file.name, target);
    return {
      blob: new Blob([output], { type: `${mime};charset=utf-8` }),
      fileName,
      metadata: {
        name: fileName,
        size: new TextEncoder().encode(output).length,
        type: { extension: target, mime, category: 'document' },
      },
      fidelity,
      verificationPassed: verification.passed,
      verificationDetails: verification.details,
    };
  }
}

/**
 * 源格式判定：内容层的结构化判定优先（例如 .txt 里其实是 JSON），
 * 但「文本兜底」结论不足以覆盖扩展名——否则损坏的 CSV 会被判成 txt，
 * 从而错报「不支持该组合」而不是「文件损坏」。
 */
export function resolveSourceFormat(bytes: Uint8Array, fileName: string): string {
  const declared = normalizeFormat(extensionOf(fileName));
  const detected = sniffFormat(bytes);
  const sniffed = detected ? normalizeFormat(detected.extension) : null;
  if (sniffed && sniffed !== 'txt' && STRUCTURED_SOURCE_FORMATS.includes(sniffed)) return sniffed;
  return STRUCTURED_SOURCE_FORMATS.includes(declared) ? declared : (sniffed ?? declared);
}

function normalizeFormat(extension: string): string {
  const canonical = canonicalExtension(extension);
  if (canonical === 'yml') return 'yaml';
  if (canonical === 'tab') return 'tsv';
  return canonical;
}

function compareFields(expected: string[], actual: string[]): boolean {
  if (expected.length !== actual.length) return false;
  return expected.every((field, index) => field === actual[index]);
}
